import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { uploadSignedErrorMessage } from "./contractErrors";

interface UploadSignedDialogProps {
  visible: boolean;
  onHide: () => void;
  // Returns true on success (dialog closes); throws on server error (mapped inline).
  onSubmit: (pdf: File) => Promise<boolean>;
}

const isPdf = (f: File): boolean =>
  f.type === "application/pdf" || /\.pdf$/i.test(f.name);

/**
 * US2 (manual track): upload the countersigned PDF. Rejects non-PDF files
 * client-side (FR-006) and maps server rejections to an inline message while
 * staying open. The selected File is a Blob and is sent as raw octet-stream
 * bytes by the service.
 */
const UploadSignedDialog: React.FC<UploadSignedDialogProps> = ({
  visible,
  onHide,
  onSubmit,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!visible) {
      setFile(null);
      setError(null);
      setSubmitting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [visible]);

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isPdf(f)) {
      setError("Please choose a PDF file.");
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  };

  const submit = async () => {
    if (!file) {
      setError("Please choose the signed PDF.");
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit(file);
      if (ok) onHide();
    } catch (e) {
      setError(uploadSignedErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Upload signed contract"
        icon="pi pi-upload"
        onClick={submit}
        loading={submitting}
        disabled={submitting || !file}
      />
    </div>
  );

  return (
    <Dialog
      header="Upload signed contract"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "32rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="text-500 mt-0">
        Choose the countersigned agreement as a PDF file.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        aria-label="Signed contract PDF"
        className="hidden"
        onChange={onSelect}
      />
      <Button
        label={file ? file.name : "Choose PDF"}
        icon="pi pi-paperclip"
        outlined
        severity="secondary"
        onClick={() => inputRef.current?.click()}
      />
    </Dialog>
  );
};

export default UploadSignedDialog;
